package com.swetdot.swetgram.cache

import app.cash.sqldelight.Query
import app.cash.sqldelight.TransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.Long
import kotlin.String

public class CacheQueries(
  driver: SqlDriver,
) : TransacterImpl(driver) {
  public fun <T : Any> selectUser(id: String, mapper: (
    id: String,
    username: String,
    displayName: String,
    avatarUrl: String?,
    bio: String?,
    location: String?,
    dateOfBirth: String?,
    showBirthYear: Long,
    isOnline: Long,
    lastSeenAt: Long?,
    isDeleted: Long,
    hasBlackBadge: Long,
  ) -> T): Query<T> = SelectUserQuery(id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getString(4),
      cursor.getString(5),
      cursor.getString(6),
      cursor.getLong(7)!!,
      cursor.getLong(8)!!,
      cursor.getLong(9),
      cursor.getLong(10)!!,
      cursor.getLong(11)!!
    )
  }

  public fun selectUser(id: String): Query<CachedUser> = selectUser(id, ::CachedUser)

  public fun <T : Any> selectChats(isArchived: Long, mapper: (
    id: String,
    kind: String,
    title: String?,
    avatarUrl: String?,
    unreadCount: Long,
    isMuted: Long,
    isArchived: Long,
    isPinned: Long,
    updatedAt: Long,
    changeCursor: String?,
  ) -> T): Query<T> = SelectChatsQuery(isArchived) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getString(3),
      cursor.getLong(4)!!,
      cursor.getLong(5)!!,
      cursor.getLong(6)!!,
      cursor.getLong(7)!!,
      cursor.getLong(8)!!,
      cursor.getString(9)
    )
  }

  public fun selectChats(isArchived: Long): Query<CachedChat> = selectChats(isArchived, ::CachedChat)

  public fun <T : Any> selectChat(id: String, mapper: (
    id: String,
    kind: String,
    title: String?,
    avatarUrl: String?,
    unreadCount: Long,
    isMuted: Long,
    isArchived: Long,
    isPinned: Long,
    updatedAt: Long,
    changeCursor: String?,
  ) -> T): Query<T> = SelectChatQuery(id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getString(3),
      cursor.getLong(4)!!,
      cursor.getLong(5)!!,
      cursor.getLong(6)!!,
      cursor.getLong(7)!!,
      cursor.getLong(8)!!,
      cursor.getString(9)
    )
  }

  public fun selectChat(id: String): Query<CachedChat> = selectChat(id, ::CachedChat)

  public fun <T : Any> selectMembers(chatId: String, mapper: (
    id: String,
    username: String,
    displayName: String,
    avatarUrl: String?,
    bio: String?,
    location: String?,
    dateOfBirth: String?,
    showBirthYear: Long,
    isOnline: Long,
    lastSeenAt: Long?,
    isDeleted: Long,
    hasBlackBadge: Long,
  ) -> T): Query<T> = SelectMembersQuery(chatId) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getString(4),
      cursor.getString(5),
      cursor.getString(6),
      cursor.getLong(7)!!,
      cursor.getLong(8)!!,
      cursor.getLong(9),
      cursor.getLong(10)!!,
      cursor.getLong(11)!!
    )
  }

  public fun selectMembers(chatId: String): Query<CachedUser> = selectMembers(chatId, ::CachedUser)

  public fun <T : Any> selectTimeline(
    chatId: String,
    `value`: Long,
    mapper: (
      chatId: String,
      clientId: String,
      serverId: String?,
      senderId: String,
      kind: String,
      text: String?,
      imageUrl: String?,
      voiceUrl: String?,
      voiceDurationMs: Long?,
      voiceWaveformJson: String?,
      replyToId: String?,
      replyToSenderId: String?,
      replyToSenderDisplayName: String?,
      replyToText: String?,
      replyToKind: String?,
      state: String,
      sentAt: Long,
      editedAt: Long?,
      isDeleted: Long,
      version: Long,
      canEdit: Long,
      canDeleteForEveryone: Long,
      reactionsJson: String?,
      picsoId: String?,
      picsoExpiresAt: String?,
      idempotencyKey: String?,
      retryCount: Long,
    ) -> T,
  ): Query<T> = SelectTimelineQuery(chatId, value) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7),
      cursor.getLong(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11),
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15)!!,
      cursor.getLong(16)!!,
      cursor.getLong(17),
      cursor.getLong(18)!!,
      cursor.getLong(19)!!,
      cursor.getLong(20)!!,
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getLong(26)!!
    )
  }

  public fun selectTimeline(chatId: String, value_: Long): Query<CachedMessage> = selectTimeline(chatId, value_, ::CachedMessage)

  public fun <T : Any> selectTimelineBefore(
    chatId: String,
    sentAt: Long,
    `value`: Long,
    mapper: (
      chatId: String,
      clientId: String,
      serverId: String?,
      senderId: String,
      kind: String,
      text: String?,
      imageUrl: String?,
      voiceUrl: String?,
      voiceDurationMs: Long?,
      voiceWaveformJson: String?,
      replyToId: String?,
      replyToSenderId: String?,
      replyToSenderDisplayName: String?,
      replyToText: String?,
      replyToKind: String?,
      state: String,
      sentAt: Long,
      editedAt: Long?,
      isDeleted: Long,
      version: Long,
      canEdit: Long,
      canDeleteForEveryone: Long,
      reactionsJson: String?,
      picsoId: String?,
      picsoExpiresAt: String?,
      idempotencyKey: String?,
      retryCount: Long,
    ) -> T,
  ): Query<T> = SelectTimelineBeforeQuery(chatId, sentAt, value) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7),
      cursor.getLong(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11),
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15)!!,
      cursor.getLong(16)!!,
      cursor.getLong(17),
      cursor.getLong(18)!!,
      cursor.getLong(19)!!,
      cursor.getLong(20)!!,
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getLong(26)!!
    )
  }

  public fun selectTimelineBefore(
    chatId: String,
    sentAt: Long,
    value_: Long,
  ): Query<CachedMessage> = selectTimelineBefore(chatId, sentAt, value_, ::CachedMessage)

  public fun <T : Any> selectMessageByClientId(
    chatId: String,
    clientId: String,
    mapper: (
      chatId: String,
      clientId: String,
      serverId: String?,
      senderId: String,
      kind: String,
      text: String?,
      imageUrl: String?,
      voiceUrl: String?,
      voiceDurationMs: Long?,
      voiceWaveformJson: String?,
      replyToId: String?,
      replyToSenderId: String?,
      replyToSenderDisplayName: String?,
      replyToText: String?,
      replyToKind: String?,
      state: String,
      sentAt: Long,
      editedAt: Long?,
      isDeleted: Long,
      version: Long,
      canEdit: Long,
      canDeleteForEveryone: Long,
      reactionsJson: String?,
      picsoId: String?,
      picsoExpiresAt: String?,
      idempotencyKey: String?,
      retryCount: Long,
    ) -> T,
  ): Query<T> = SelectMessageByClientIdQuery(chatId, clientId) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7),
      cursor.getLong(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11),
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15)!!,
      cursor.getLong(16)!!,
      cursor.getLong(17),
      cursor.getLong(18)!!,
      cursor.getLong(19)!!,
      cursor.getLong(20)!!,
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getLong(26)!!
    )
  }

  public fun selectMessageByClientId(chatId: String, clientId: String): Query<CachedMessage> = selectMessageByClientId(chatId, clientId, ::CachedMessage)

  public fun <T : Any> selectLastMessage(chatId: String, mapper: (
    chatId: String,
    clientId: String,
    serverId: String?,
    senderId: String,
    kind: String,
    text: String?,
    imageUrl: String?,
    voiceUrl: String?,
    voiceDurationMs: Long?,
    voiceWaveformJson: String?,
    replyToId: String?,
    replyToSenderId: String?,
    replyToSenderDisplayName: String?,
    replyToText: String?,
    replyToKind: String?,
    state: String,
    sentAt: Long,
    editedAt: Long?,
    isDeleted: Long,
    version: Long,
    canEdit: Long,
    canDeleteForEveryone: Long,
    reactionsJson: String?,
    picsoId: String?,
    picsoExpiresAt: String?,
    idempotencyKey: String?,
    retryCount: Long,
  ) -> T): Query<T> = SelectLastMessageQuery(chatId) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7),
      cursor.getLong(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11),
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15)!!,
      cursor.getLong(16)!!,
      cursor.getLong(17),
      cursor.getLong(18)!!,
      cursor.getLong(19)!!,
      cursor.getLong(20)!!,
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getLong(26)!!
    )
  }

  public fun selectLastMessage(chatId: String): Query<CachedMessage> = selectLastMessage(chatId, ::CachedMessage)

  public fun <T : Any> selectPending(mapper: (
    chatId: String,
    clientId: String,
    serverId: String?,
    senderId: String,
    kind: String,
    text: String?,
    imageUrl: String?,
    voiceUrl: String?,
    voiceDurationMs: Long?,
    voiceWaveformJson: String?,
    replyToId: String?,
    replyToSenderId: String?,
    replyToSenderDisplayName: String?,
    replyToText: String?,
    replyToKind: String?,
    state: String,
    sentAt: Long,
    editedAt: Long?,
    isDeleted: Long,
    version: Long,
    canEdit: Long,
    canDeleteForEveryone: Long,
    reactionsJson: String?,
    picsoId: String?,
    picsoExpiresAt: String?,
    idempotencyKey: String?,
    retryCount: Long,
  ) -> T): Query<T> = Query(1_060_306_466, arrayOf("cachedMessage"), driver, "Cache.sq", "selectPending", "SELECT cachedMessage.chatId, cachedMessage.clientId, cachedMessage.serverId, cachedMessage.senderId, cachedMessage.kind, cachedMessage.text, cachedMessage.imageUrl, cachedMessage.voiceUrl, cachedMessage.voiceDurationMs, cachedMessage.voiceWaveformJson, cachedMessage.replyToId, cachedMessage.replyToSenderId, cachedMessage.replyToSenderDisplayName, cachedMessage.replyToText, cachedMessage.replyToKind, cachedMessage.state, cachedMessage.sentAt, cachedMessage.editedAt, cachedMessage.isDeleted, cachedMessage.version, cachedMessage.canEdit, cachedMessage.canDeleteForEveryone, cachedMessage.reactionsJson, cachedMessage.picsoId, cachedMessage.picsoExpiresAt, cachedMessage.idempotencyKey, cachedMessage.retryCount FROM cachedMessage WHERE state IN ('SENDING', 'FAILED') ORDER BY sentAt ASC") { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7),
      cursor.getLong(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11),
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15)!!,
      cursor.getLong(16)!!,
      cursor.getLong(17),
      cursor.getLong(18)!!,
      cursor.getLong(19)!!,
      cursor.getLong(20)!!,
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getLong(26)!!
    )
  }

  public fun selectPending(): Query<CachedMessage> = selectPending(::CachedMessage)

  public fun <T : Any> selectCachedDiscoverFeed(feedKey: String, mapper: (
    feedKey: String,
    postsJson: String,
    olderCursor: String?,
    newerCursor: String?,
    validatorRequestKey: String?,
    validatorEtag: String?,
    fetchedAt: Long,
  ) -> T): Query<T> = SelectCachedDiscoverFeedQuery(feedKey) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getString(3),
      cursor.getString(4),
      cursor.getString(5),
      cursor.getLong(6)!!
    )
  }

  public fun selectCachedDiscoverFeed(feedKey: String): Query<CachedDiscoverFeed> = selectCachedDiscoverFeed(feedKey, ::CachedDiscoverFeed)

  public fun <T : Any> selectCachedDailyTasks(cacheKey: String, mapper: (
    cacheKey: String,
    tasksJson: String,
    fetchedAt: Long,
  ) -> T): Query<T> = SelectCachedDailyTasksQuery(cacheKey) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getLong(2)!!
    )
  }

  public fun selectCachedDailyTasks(cacheKey: String): Query<CachedDailyTasks> = selectCachedDailyTasks(cacheKey, ::CachedDailyTasks)

  /**
   * @return The number of rows updated.
   */
  public fun upsertUser(
    id: String,
    username: String,
    displayName: String,
    avatarUrl: String?,
    bio: String?,
    location: String?,
    dateOfBirth: String?,
    showBirthYear: Long,
    isOnline: Long,
    lastSeenAt: Long?,
    isDeleted: Long,
    hasBlackBadge: Long,
  ): QueryResult<Long> {
    val result = driver.execute(1_236_880_851, """
        |INSERT INTO cachedUser(id, username, displayName, avatarUrl, bio, location, dateOfBirth, showBirthYear, isOnline, lastSeenAt, isDeleted, hasBlackBadge)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        |ON CONFLICT(id) DO UPDATE SET
        |    username = excluded.username,
        |    displayName = excluded.displayName,
        |    avatarUrl = excluded.avatarUrl,
        |    bio = excluded.bio,
        |    location = excluded.location,
        |    dateOfBirth = excluded.dateOfBirth,
        |    showBirthYear = excluded.showBirthYear,
        |    isOnline = excluded.isOnline,
        |    lastSeenAt = excluded.lastSeenAt,
        |    isDeleted = excluded.isDeleted,
        |    hasBlackBadge = excluded.hasBlackBadge
        """.trimMargin(), 12) {
          var parameterIndex = 0
          bindString(parameterIndex++, id)
          bindString(parameterIndex++, username)
          bindString(parameterIndex++, displayName)
          bindString(parameterIndex++, avatarUrl)
          bindString(parameterIndex++, bio)
          bindString(parameterIndex++, location)
          bindString(parameterIndex++, dateOfBirth)
          bindLong(parameterIndex++, showBirthYear)
          bindLong(parameterIndex++, isOnline)
          bindLong(parameterIndex++, lastSeenAt)
          bindLong(parameterIndex++, isDeleted)
          bindLong(parameterIndex++, hasBlackBadge)
        }
    notifyQueries(1_236_880_851) { emit ->
      emit("cachedUser")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun upsertChat(
    id: String,
    kind: String,
    title: String?,
    avatarUrl: String?,
    unreadCount: Long,
    isMuted: Long,
    isArchived: Long,
    isPinned: Long,
    updatedAt: Long,
    changeCursor: String?,
  ): QueryResult<Long> {
    val result = driver.execute(1_236_333_920, """
        |INSERT INTO cachedChat(id, kind, title, avatarUrl, unreadCount, isMuted, isArchived, isPinned, updatedAt, changeCursor)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        |ON CONFLICT(id) DO UPDATE SET
        |    kind = excluded.kind,
        |    title = excluded.title,
        |    avatarUrl = excluded.avatarUrl,
        |    unreadCount = excluded.unreadCount,
        |    isMuted = excluded.isMuted,
        |    isArchived = excluded.isArchived,
        |    isPinned = excluded.isPinned,
        |    updatedAt = excluded.updatedAt,
        |    -- Never clobber a stored cursor with NULL: a chat-list refresh does not carry one, and
        |    -- losing it would silently disable reconciliation for that chat.
        |    changeCursor = COALESCE(excluded.changeCursor, cachedChat.changeCursor)
        """.trimMargin(), 10) {
          var parameterIndex = 0
          bindString(parameterIndex++, id)
          bindString(parameterIndex++, kind)
          bindString(parameterIndex++, title)
          bindString(parameterIndex++, avatarUrl)
          bindLong(parameterIndex++, unreadCount)
          bindLong(parameterIndex++, isMuted)
          bindLong(parameterIndex++, isArchived)
          bindLong(parameterIndex++, isPinned)
          bindLong(parameterIndex++, updatedAt)
          bindString(parameterIndex++, changeCursor)
        }
    notifyQueries(1_236_333_920) { emit ->
      emit("cachedChat")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun setChangeCursor(changeCursor: String?, id: String): QueryResult<Long> {
    val result = driver.execute(-1_879_387_121, """UPDATE cachedChat SET changeCursor = ? WHERE id = ?""", 2) {
          var parameterIndex = 0
          bindString(parameterIndex++, changeCursor)
          bindString(parameterIndex++, id)
        }
    notifyQueries(-1_879_387_121) { emit ->
      emit("cachedChat")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun replaceMembers(chatId: String, userId: String): QueryResult<Long> {
    val result = driver.execute(-1_237_632_130, """INSERT OR REPLACE INTO cachedChatMember(chatId, userId) VALUES (?, ?)""", 2) {
          var parameterIndex = 0
          bindString(parameterIndex++, chatId)
          bindString(parameterIndex++, userId)
        }
    notifyQueries(-1_237_632_130) { emit ->
      emit("cachedChatMember")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteChat(id: String): QueryResult<Long> {
    val result = driver.execute(-582_106_148, """DELETE FROM cachedChat WHERE id = ?""", 1) {
          var parameterIndex = 0
          bindString(parameterIndex++, id)
        }
    notifyQueries(-582_106_148) { emit ->
      emit("cachedChat")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteChatMembers(chatId: String): QueryResult<Long> {
    val result = driver.execute(1_753_927_069, """DELETE FROM cachedChatMember WHERE chatId = ?""", 1) {
          var parameterIndex = 0
          bindString(parameterIndex++, chatId)
        }
    notifyQueries(1_753_927_069) { emit ->
      emit("cachedChatMember")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteChatMessages(chatId: String): QueryResult<Long> {
    val result = driver.execute(-1_275_491_000, """DELETE FROM cachedMessage WHERE chatId = ?""", 1) {
          var parameterIndex = 0
          bindString(parameterIndex++, chatId)
        }
    notifyQueries(-1_275_491_000) { emit ->
      emit("cachedMessage")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun upsertMessage(
    chatId: String,
    clientId: String,
    serverId: String?,
    senderId: String,
    kind: String,
    text: String?,
    imageUrl: String?,
    voiceUrl: String?,
    voiceDurationMs: Long?,
    voiceWaveformJson: String?,
    replyToId: String?,
    replyToSenderId: String?,
    replyToSenderDisplayName: String?,
    replyToText: String?,
    replyToKind: String?,
    state: String,
    sentAt: Long,
    editedAt: Long?,
    isDeleted: Long,
    version: Long,
    canEdit: Long,
    canDeleteForEveryone: Long,
    reactionsJson: String?,
    picsoId: String?,
    picsoExpiresAt: String?,
    idempotencyKey: String?,
    retryCount: Long,
  ): QueryResult<Long> {
    val result = driver.execute(-1_799_814_913, """
        |INSERT INTO cachedMessage(
        |    chatId, clientId, serverId, senderId, kind, text, imageUrl, voiceUrl, voiceDurationMs, voiceWaveformJson,
        |    replyToId, replyToSenderId, replyToSenderDisplayName, replyToText, replyToKind, state, sentAt, editedAt,
        |    isDeleted, version, canEdit, canDeleteForEveryone, reactionsJson, picsoId, picsoExpiresAt, idempotencyKey, retryCount)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        |ON CONFLICT(chatId, clientId) DO UPDATE SET
        |    serverId = COALESCE(excluded.serverId, cachedMessage.serverId),
        |    text = excluded.text,
        |    imageUrl = excluded.imageUrl,
        |    voiceUrl = excluded.voiceUrl,
        |    voiceDurationMs = excluded.voiceDurationMs,
        |    voiceWaveformJson = excluded.voiceWaveformJson,
        |    replyToId = COALESCE(excluded.replyToId, cachedMessage.replyToId),
        |    replyToSenderId = COALESCE(excluded.replyToSenderId, cachedMessage.replyToSenderId),
        |    replyToSenderDisplayName = COALESCE(excluded.replyToSenderDisplayName, cachedMessage.replyToSenderDisplayName),
        |    replyToText = COALESCE(excluded.replyToText, cachedMessage.replyToText),
        |    replyToKind = COALESCE(excluded.replyToKind, cachedMessage.replyToKind),
        |    state = excluded.state,
        |    editedAt = excluded.editedAt,
        |    isDeleted = excluded.isDeleted,
        |    -- Never move a resource version backwards; a stale response must not undo a newer edit.
        |    version = MAX(excluded.version, cachedMessage.version),
        |    canEdit = excluded.canEdit,
        |    canDeleteForEveryone = excluded.canDeleteForEveryone,
        |    reactionsJson = excluded.reactionsJson,
        |    picsoId = excluded.picsoId,
        |    picsoExpiresAt = excluded.picsoExpiresAt,
        |    -- Keep the ORIGINAL key so a retry stays idempotent against the server.
        |    idempotencyKey = COALESCE(cachedMessage.idempotencyKey, excluded.idempotencyKey)
        """.trimMargin(), 27) {
          var parameterIndex = 0
          bindString(parameterIndex++, chatId)
          bindString(parameterIndex++, clientId)
          bindString(parameterIndex++, serverId)
          bindString(parameterIndex++, senderId)
          bindString(parameterIndex++, kind)
          bindString(parameterIndex++, text)
          bindString(parameterIndex++, imageUrl)
          bindString(parameterIndex++, voiceUrl)
          bindLong(parameterIndex++, voiceDurationMs)
          bindString(parameterIndex++, voiceWaveformJson)
          bindString(parameterIndex++, replyToId)
          bindString(parameterIndex++, replyToSenderId)
          bindString(parameterIndex++, replyToSenderDisplayName)
          bindString(parameterIndex++, replyToText)
          bindString(parameterIndex++, replyToKind)
          bindString(parameterIndex++, state)
          bindLong(parameterIndex++, sentAt)
          bindLong(parameterIndex++, editedAt)
          bindLong(parameterIndex++, isDeleted)
          bindLong(parameterIndex++, version)
          bindLong(parameterIndex++, canEdit)
          bindLong(parameterIndex++, canDeleteForEveryone)
          bindString(parameterIndex++, reactionsJson)
          bindString(parameterIndex++, picsoId)
          bindString(parameterIndex++, picsoExpiresAt)
          bindString(parameterIndex++, idempotencyKey)
          bindLong(parameterIndex++, retryCount)
        }
    notifyQueries(-1_799_814_913) { emit ->
      emit("cachedMessage")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun markState(
    state: String,
    chatId: String,
    clientId: String,
  ): QueryResult<Long> {
    val result = driver.execute(913_577_579, """UPDATE cachedMessage SET state = ? WHERE chatId = ? AND clientId = ?""", 3) {
          var parameterIndex = 0
          bindString(parameterIndex++, state)
          bindString(parameterIndex++, chatId)
          bindString(parameterIndex++, clientId)
        }
    notifyQueries(913_577_579) { emit ->
      emit("cachedMessage")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun markFailed(chatId: String, clientId: String): QueryResult<Long> {
    val result = driver.execute(-2_133_361_245, """
        |UPDATE cachedMessage SET state = 'FAILED', retryCount = retryCount + 1
        |WHERE chatId = ? AND clientId = ?
        """.trimMargin(), 2) {
          var parameterIndex = 0
          bindString(parameterIndex++, chatId)
          bindString(parameterIndex++, clientId)
        }
    notifyQueries(-2_133_361_245) { emit ->
      emit("cachedMessage")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun applyTombstone(serverId: String?): QueryResult<Long> {
    val result = driver.execute(null, """
        |UPDATE cachedMessage SET isDeleted = 1, text = NULL, imageUrl = NULL, voiceUrl = NULL,
        |    voiceDurationMs = NULL, voiceWaveformJson = NULL WHERE serverId ${ if (serverId == null) "IS" else "=" } ?
        """.trimMargin(), 1) {
          var parameterIndex = 0
          bindString(parameterIndex++, serverId)
        }
    notifyQueries(-741_099_264) { emit ->
      emit("cachedMessage")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteMessage(chatId: String, clientId: String): QueryResult<Long> {
    val result = driver.execute(1_769_591_043, """DELETE FROM cachedMessage WHERE chatId = ? AND clientId = ?""", 2) {
          var parameterIndex = 0
          bindString(parameterIndex++, chatId)
          bindString(parameterIndex++, clientId)
        }
    notifyQueries(1_769_591_043) { emit ->
      emit("cachedMessage")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun clearAllMessages(): QueryResult<Long> {
    val result = driver.execute(1_976_488_697, """DELETE FROM cachedMessage""", 0)
    notifyQueries(1_976_488_697) { emit ->
      emit("cachedMessage")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun clearAllChatMembers(): QueryResult<Long> {
    val result = driver.execute(1_364_987_348, """DELETE FROM cachedChatMember""", 0)
    notifyQueries(1_364_987_348) { emit ->
      emit("cachedChatMember")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun clearAllChats(): QueryResult<Long> {
    val result = driver.execute(2_053_254_542, """DELETE FROM cachedChat""", 0)
    notifyQueries(2_053_254_542) { emit ->
      emit("cachedChat")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun clearAllUsers(): QueryResult<Long> {
    val result = driver.execute(2_070_209_403, """DELETE FROM cachedUser""", 0)
    notifyQueries(2_070_209_403) { emit ->
      emit("cachedUser")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun upsertCachedDiscoverFeed(
    feedKey: String,
    postsJson: String,
    olderCursor: String?,
    newerCursor: String?,
    validatorRequestKey: String?,
    validatorEtag: String?,
    fetchedAt: Long,
  ): QueryResult<Long> {
    val result = driver.execute(880_925_553, """
        |INSERT INTO cachedDiscoverFeed(
        |    feedKey, postsJson, olderCursor, newerCursor, validatorRequestKey, validatorEtag, fetchedAt
        |)
        |VALUES (?, ?, ?, ?, ?, ?, ?)
        |ON CONFLICT(feedKey) DO UPDATE SET
        |    postsJson = excluded.postsJson,
        |    olderCursor = excluded.olderCursor,
        |    newerCursor = excluded.newerCursor,
        |    validatorRequestKey = excluded.validatorRequestKey,
        |    validatorEtag = excluded.validatorEtag,
        |    fetchedAt = excluded.fetchedAt
        """.trimMargin(), 7) {
          var parameterIndex = 0
          bindString(parameterIndex++, feedKey)
          bindString(parameterIndex++, postsJson)
          bindString(parameterIndex++, olderCursor)
          bindString(parameterIndex++, newerCursor)
          bindString(parameterIndex++, validatorRequestKey)
          bindString(parameterIndex++, validatorEtag)
          bindLong(parameterIndex++, fetchedAt)
        }
    notifyQueries(880_925_553) { emit ->
      emit("cachedDiscoverFeed")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun touchCachedDiscoverFeed(fetchedAt: Long, feedKey: String): QueryResult<Long> {
    val result = driver.execute(2_051_469_679, """UPDATE cachedDiscoverFeed SET fetchedAt = ? WHERE feedKey = ?""", 2) {
          var parameterIndex = 0
          bindLong(parameterIndex++, fetchedAt)
          bindString(parameterIndex++, feedKey)
        }
    notifyQueries(2_051_469_679) { emit ->
      emit("cachedDiscoverFeed")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteCachedDiscoverFeed(feedKey: String): QueryResult<Long> {
    val result = driver.execute(1_558_552_813, """DELETE FROM cachedDiscoverFeed WHERE feedKey = ?""", 1) {
          var parameterIndex = 0
          bindString(parameterIndex++, feedKey)
        }
    notifyQueries(1_558_552_813) { emit ->
      emit("cachedDiscoverFeed")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun clearAllDiscoverFeeds(): QueryResult<Long> {
    val result = driver.execute(-1_453_055_329, """DELETE FROM cachedDiscoverFeed""", 0)
    notifyQueries(-1_453_055_329) { emit ->
      emit("cachedDiscoverFeed")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun upsertCachedDailyTasks(
    cacheKey: String,
    tasksJson: String,
    fetchedAt: Long,
  ): QueryResult<Long> {
    val result = driver.execute(1_011_074_495, """
        |INSERT INTO cachedDailyTasks(cacheKey, tasksJson, fetchedAt)
        |VALUES (?, ?, ?)
        |ON CONFLICT(cacheKey) DO UPDATE SET
        |    tasksJson = excluded.tasksJson,
        |    fetchedAt = excluded.fetchedAt
        """.trimMargin(), 3) {
          var parameterIndex = 0
          bindString(parameterIndex++, cacheKey)
          bindString(parameterIndex++, tasksJson)
          bindLong(parameterIndex++, fetchedAt)
        }
    notifyQueries(1_011_074_495) { emit ->
      emit("cachedDailyTasks")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun clearAllDailyTasks(): QueryResult<Long> {
    val result = driver.execute(-57_757_854, """DELETE FROM cachedDailyTasks""", 0)
    notifyQueries(-57_757_854) { emit ->
      emit("cachedDailyTasks")
    }
    return result
  }

  private inner class SelectUserQuery<out T : Any>(
    public val id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("cachedUser", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("cachedUser", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(292_862_752, """SELECT cachedUser.id, cachedUser.username, cachedUser.displayName, cachedUser.avatarUrl, cachedUser.bio, cachedUser.location, cachedUser.dateOfBirth, cachedUser.showBirthYear, cachedUser.isOnline, cachedUser.lastSeenAt, cachedUser.isDeleted, cachedUser.hasBlackBadge FROM cachedUser WHERE id = ?""", mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, id)
    }

    override fun toString(): String = "Cache.sq:selectUser"
  }

  private inner class SelectChatsQuery<out T : Any>(
    public val isArchived: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("cachedChat", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("cachedChat", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(471_855_974, """SELECT cachedChat.id, cachedChat.kind, cachedChat.title, cachedChat.avatarUrl, cachedChat.unreadCount, cachedChat.isMuted, cachedChat.isArchived, cachedChat.isPinned, cachedChat.updatedAt, cachedChat.changeCursor FROM cachedChat WHERE isArchived = ? ORDER BY isPinned DESC, updatedAt DESC""", mapper, 1) {
      var parameterIndex = 0
      bindLong(parameterIndex++, isArchived)
    }

    override fun toString(): String = "Cache.sq:selectChats"
  }

  private inner class SelectChatQuery<out T : Any>(
    public val id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("cachedChat", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("cachedChat", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(292_315_821, """SELECT cachedChat.id, cachedChat.kind, cachedChat.title, cachedChat.avatarUrl, cachedChat.unreadCount, cachedChat.isMuted, cachedChat.isArchived, cachedChat.isPinned, cachedChat.updatedAt, cachedChat.changeCursor FROM cachedChat WHERE id = ?""", mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, id)
    }

    override fun toString(): String = "Cache.sq:selectChat"
  }

  private inner class SelectMembersQuery<out T : Any>(
    public val chatId: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("cachedUser", "cachedChatMember", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("cachedUser", "cachedChatMember", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(-1_603_191_388, """SELECT u.id, u.username, u.displayName, u.avatarUrl, u.bio, u.location, u.dateOfBirth, u.showBirthYear, u.isOnline, u.lastSeenAt, u.isDeleted, u.hasBlackBadge FROM cachedChatMember m JOIN cachedUser u ON u.id = m.userId WHERE m.chatId = ?""", mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, chatId)
    }

    override fun toString(): String = "Cache.sq:selectMembers"
  }

  private inner class SelectTimelineQuery<out T : Any>(
    public val chatId: String,
    public val `value`: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("cachedMessage", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("cachedMessage", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(413_462_806, """
    |SELECT cachedMessage.chatId, cachedMessage.clientId, cachedMessage.serverId, cachedMessage.senderId, cachedMessage.kind, cachedMessage.text, cachedMessage.imageUrl, cachedMessage.voiceUrl, cachedMessage.voiceDurationMs, cachedMessage.voiceWaveformJson, cachedMessage.replyToId, cachedMessage.replyToSenderId, cachedMessage.replyToSenderDisplayName, cachedMessage.replyToText, cachedMessage.replyToKind, cachedMessage.state, cachedMessage.sentAt, cachedMessage.editedAt, cachedMessage.isDeleted, cachedMessage.version, cachedMessage.canEdit, cachedMessage.canDeleteForEveryone, cachedMessage.reactionsJson, cachedMessage.picsoId, cachedMessage.picsoExpiresAt, cachedMessage.idempotencyKey, cachedMessage.retryCount FROM cachedMessage
    |WHERE chatId = ? AND isDeleted = 0
    |ORDER BY sentAt DESC, clientId DESC
    |LIMIT ?
    """.trimMargin(), mapper, 2) {
      var parameterIndex = 0
      bindString(parameterIndex++, chatId)
      bindLong(parameterIndex++, value)
    }

    override fun toString(): String = "Cache.sq:selectTimeline"
  }

  private inner class SelectTimelineBeforeQuery<out T : Any>(
    public val chatId: String,
    public val sentAt: Long,
    public val `value`: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("cachedMessage", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("cachedMessage", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(-845_908_939, """
    |SELECT cachedMessage.chatId, cachedMessage.clientId, cachedMessage.serverId, cachedMessage.senderId, cachedMessage.kind, cachedMessage.text, cachedMessage.imageUrl, cachedMessage.voiceUrl, cachedMessage.voiceDurationMs, cachedMessage.voiceWaveformJson, cachedMessage.replyToId, cachedMessage.replyToSenderId, cachedMessage.replyToSenderDisplayName, cachedMessage.replyToText, cachedMessage.replyToKind, cachedMessage.state, cachedMessage.sentAt, cachedMessage.editedAt, cachedMessage.isDeleted, cachedMessage.version, cachedMessage.canEdit, cachedMessage.canDeleteForEveryone, cachedMessage.reactionsJson, cachedMessage.picsoId, cachedMessage.picsoExpiresAt, cachedMessage.idempotencyKey, cachedMessage.retryCount FROM cachedMessage
    |WHERE chatId = ? AND isDeleted = 0 AND sentAt < ?
    |ORDER BY sentAt DESC, clientId DESC
    |LIMIT ?
    """.trimMargin(), mapper, 3) {
      var parameterIndex = 0
      bindString(parameterIndex++, chatId)
      bindLong(parameterIndex++, sentAt)
      bindLong(parameterIndex++, value)
    }

    override fun toString(): String = "Cache.sq:selectTimelineBefore"
  }

  private inner class SelectMessageByClientIdQuery<out T : Any>(
    public val chatId: String,
    public val clientId: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("cachedMessage", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("cachedMessage", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(-1_723_751_953, """SELECT cachedMessage.chatId, cachedMessage.clientId, cachedMessage.serverId, cachedMessage.senderId, cachedMessage.kind, cachedMessage.text, cachedMessage.imageUrl, cachedMessage.voiceUrl, cachedMessage.voiceDurationMs, cachedMessage.voiceWaveformJson, cachedMessage.replyToId, cachedMessage.replyToSenderId, cachedMessage.replyToSenderDisplayName, cachedMessage.replyToText, cachedMessage.replyToKind, cachedMessage.state, cachedMessage.sentAt, cachedMessage.editedAt, cachedMessage.isDeleted, cachedMessage.version, cachedMessage.canEdit, cachedMessage.canDeleteForEveryone, cachedMessage.reactionsJson, cachedMessage.picsoId, cachedMessage.picsoExpiresAt, cachedMessage.idempotencyKey, cachedMessage.retryCount FROM cachedMessage WHERE chatId = ? AND clientId = ?""", mapper, 2) {
      var parameterIndex = 0
      bindString(parameterIndex++, chatId)
      bindString(parameterIndex++, clientId)
    }

    override fun toString(): String = "Cache.sq:selectMessageByClientId"
  }

  private inner class SelectLastMessageQuery<out T : Any>(
    public val chatId: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("cachedMessage", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("cachedMessage", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(-1_167_571_044, """
    |SELECT cachedMessage.chatId, cachedMessage.clientId, cachedMessage.serverId, cachedMessage.senderId, cachedMessage.kind, cachedMessage.text, cachedMessage.imageUrl, cachedMessage.voiceUrl, cachedMessage.voiceDurationMs, cachedMessage.voiceWaveformJson, cachedMessage.replyToId, cachedMessage.replyToSenderId, cachedMessage.replyToSenderDisplayName, cachedMessage.replyToText, cachedMessage.replyToKind, cachedMessage.state, cachedMessage.sentAt, cachedMessage.editedAt, cachedMessage.isDeleted, cachedMessage.version, cachedMessage.canEdit, cachedMessage.canDeleteForEveryone, cachedMessage.reactionsJson, cachedMessage.picsoId, cachedMessage.picsoExpiresAt, cachedMessage.idempotencyKey, cachedMessage.retryCount FROM cachedMessage WHERE chatId = ? AND isDeleted = 0
    |ORDER BY sentAt DESC, clientId DESC LIMIT 1
    """.trimMargin(), mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, chatId)
    }

    override fun toString(): String = "Cache.sq:selectLastMessage"
  }

  private inner class SelectCachedDiscoverFeedQuery<out T : Any>(
    public val feedKey: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("cachedDiscoverFeed", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("cachedDiscoverFeed", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(-868_647_426, """SELECT cachedDiscoverFeed.feedKey, cachedDiscoverFeed.postsJson, cachedDiscoverFeed.olderCursor, cachedDiscoverFeed.newerCursor, cachedDiscoverFeed.validatorRequestKey, cachedDiscoverFeed.validatorEtag, cachedDiscoverFeed.fetchedAt FROM cachedDiscoverFeed WHERE feedKey = ?""", mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, feedKey)
    }

    override fun toString(): String = "Cache.sq:selectCachedDiscoverFeed"
  }

  private inner class SelectCachedDailyTasksQuery<out T : Any>(
    public val cacheKey: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("cachedDailyTasks", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("cachedDailyTasks", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(-894_654_580, """SELECT cachedDailyTasks.cacheKey, cachedDailyTasks.tasksJson, cachedDailyTasks.fetchedAt FROM cachedDailyTasks WHERE cacheKey = ?""", mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, cacheKey)
    }

    override fun toString(): String = "Cache.sq:selectCachedDailyTasks"
  }
}
